/**
 * THE signature component: "полоса состояния".
 *
 * Reads like a soil section / seismograph tape. One column per Sentinel-2 acquisition,
 * old on the left, fresh on the right:
 *
 *   solid fill      — accepted; the surface class is read off the LABELLED BANDS of the
 *                     axis, so it is carried by position, not by colour;
 *   diagonal hatch  — accepted, but the measurement is doubtful (snow, thin valid cover,
 *                     no NDVI at all). Never a statement about the class;
 *   outline on the rail — rejected. It shares the time axis with the accepted columns and
 *                     does NOT share the value axis;
 *   bracket above   — the detected event; its width IS the date uncertainty;
 *   shading right   — no fresh data for N days.
 *
 * Two things changed in 2026-08 and both were misstatements, not taste (docs/UI_PLAN.md §4.2):
 *
 *   (a) a rejected date used to be drawn full height, i.e. asserting NDVI 0.9 — higher than
 *       any real reading on the field. On "1 clear shot of 15" that is a palisade 4.7x taller
 *       than the single measurement the answer rests on. It now rides a rail below the
 *       baseline: same x, same width, no claim about magnitude.
 *   (b) the value axis carried four decimal hairlines (0.2 / 0.4 / 0.6 / 0.8), none of which
 *       means anything. The meaningful marks — 0.25 bare/stubble and 0.35 stubble/canopy —
 *       are drawn as named bands instead. A digger has nothing to do with the number 0.4;
 *       he needs the word. The detector's own latch value is NOT among them: reading a
 *       surface class off greenness is textbook, but the gate is ours and stays ours.
 *
 * State is never encoded by colour alone: fill / hatch / outline / rail carry the same
 * information for anyone who cannot separate ochre from slate.
 */

import { useId, useState } from "react";

import { T } from "../strings";
import type { StripColumn, StripModel } from "../lib/strip";
import { formatDate, formatDayMonthShort, num, percent } from "../lib/format";
import {
  DISPLAY_NDVI_AXIS_MAX,
  DISPLAY_UNCERTAIN_NDVI_HIGH,
  DISPLAY_UNCERTAIN_NDVI_LOW,
} from "../lib/constants";
import { minGapFraction } from "../lib/timeline";
import { useMeasuredWidth } from "../hooks/useMeasuredWidth";
import "./StateStrip.css";

const VIEW_HEIGHT = 196;
const PAD_LEFT = 34;
const PAD_RIGHT = 16;
const BRACKET_TOP = 10;
const BRACKET_BOTTOM = 38;
const PLOT_TOP = 44;
const BASELINE = 146;
const PLOT_HEIGHT = BASELINE - PLOT_TOP;
/** The rail rejected dates ride on: below the baseline, off the value axis. */
const RAIL_TOP = 149;
const RAIL_BOTTOM = 159;
const TICK_BOTTOM = 162;
const TICK_LABEL_Y = 175;
/** Whole reveal stays inside the 400-600 ms the brief allows. */
const REVEAL_TOTAL_MS = 300;

/**
 * The tooltip, sized so the question of escaping its container never arises.
 *
 * It used to be `min-width: 13rem` positioned by `left: <pct>%` with `translateX(-50%)`. The
 * console's content box is 318.6 px, so a column in the left third put the box's left edge
 * up to 104 px outside — where `.k-console__body`'s `overflow:auto` clipped it and the
 * `backdrop-filter` on `.k-console` (its own stacking context) made `z-index: 10` mean
 * nothing. Half the tooltip ended up under the map. Both mechanisms disappear once the box
 * provably fits inside the strip: 9rem instead of 13, six numeric rows gone to the developer
 * surface, and the centre clamped to the container.
 */
const TIP_MIN_PX = 144;
const TIP_MAX_PX = 240;
const TIP_EDGE_PX = 4;
/** Below the rail: on the legend, never on the bracket the tooltip is explaining. */
const TIP_TOP = RAIL_BOTTOM - 1;

/** Named bands of the value axis: the class is a position, and the position has a word. */
const BANDS = [
  { key: "bare", from: 0, to: DISPLAY_UNCERTAIN_NDVI_LOW, label: T.strip.bands.bare },
  {
    key: "stubble",
    from: DISPLAY_UNCERTAIN_NDVI_LOW,
    to: DISPLAY_UNCERTAIN_NDVI_HIGH,
    label: T.strip.bands.stubble,
  },
  {
    key: "vegetation",
    from: DISPLAY_UNCERTAIN_NDVI_HIGH,
    to: DISPLAY_NDVI_AXIS_MAX,
    label: T.strip.bands.vegetation,
  },
] as const;

const plotWidthOf = (viewWidth: number): number => viewWidth - PAD_LEFT - PAD_RIGHT;

const columnWidth = (count: number, plotWidth: number): number =>
  Math.max(5, Math.min(20, plotWidth / Math.max(count, 1) / 1.5));

const toneClass = (column: StripColumn): string =>
  column.accepted ? `k-strip__col--${column.tone}` : "k-strip__col--rejected";

const toneWord = (column: StripColumn): string =>
  column.tone === "vegetation"
    ? T.strip.bands.vegetation
    : column.tone === "stubble"
      ? T.strip.bands.stubble
      : T.strip.bands.bare;

/**
 * What the tooltip says, in words. The numbers behind it live on the developer surface.
 *
 * ФОРМАТ — тот же, что у `stateWords` в lib/strip.ts (`columnAria`): «чистый снимок,
 * растительность» / «измерение неуверенное (…)» (state-words-single-source). Пока фраза не
 * собрана в одном месте (это правка strip.ts), формат обязан совпадать буквально: два
 * написания одной фразы на одной колонке и были дефектом.
 */
const stateWords = (column: StripColumn): string => {
  if (!column.accepted) return `${T.qa.rejected}: ${column.reasonText}`;
  return column.uncertainText
    ? `${T.qa.uncertain} (${column.uncertainText})`
    : `${T.qa.clean}, ${toneWord(column)}`;
};

/**
 * Where the tooltip goes so that it is wholly inside the strip — the fix for the reported
 * bug, stated as arithmetic instead of as a z-index. Exported because that arithmetic is the
 * thing worth a test: at the console's real width the leftmost column must not push the box
 * past the left edge.
 */
export const tooltipBox = (
  centreX: number,
  viewWidth: number,
): { left: number; minWidth: number; maxWidth: number } => {
  const room = Math.max(viewWidth - 2 * TIP_EDGE_PX, 0);
  const maxWidth = Math.min(TIP_MAX_PX, room);
  const minWidth = Math.min(TIP_MIN_PX, maxWidth);
  // Clamped against the WIDEST the box may become, so containment holds whatever the
  // sentence inside turns out to be.
  const half = maxWidth / 2;
  const lower = half + TIP_EDGE_PX;
  const upper = Math.max(viewWidth - half - TIP_EDGE_PX, lower);
  return { left: Math.min(Math.max(centreX, lower), upper), minWidth, maxWidth };
};

interface Props {
  model: StripModel;
  /** Changes when a new verdict lands: replays the reveal exactly once. */
  revealKey?: number;
  animate?: boolean;
  /** Clicking a column asks the map for that date's preview. */
  onPickDate?: (date: string) => void;
  /**
   * The date the preview is showing. Marked on the strip and read out below it, so dates
   * can be compared with the arrow keys instead of only with the mouse.
   */
  selectedDate?: string | null;
  /**
   * Developer surface (docs/UI_PLAN.md §2.2). Adds the six numeric rows to the tooltip.
   * The console owns the flag; the strip only obeys it.
   */
  dev?: boolean;
}

export function StateStrip({
  model,
  revealKey = 0,
  animate = true,
  onPickDate,
  selectedDate = null,
  dev = false,
}: Props) {
  const [hovered, setHovered] = useState<StripColumn | null>(null);
  const idPrefix = useId().replace(/:/g, "");
  const [viewWidth, wrapper] = useMeasuredWidth();
  const plotWidth = plotWidthOf(viewWidth);
  const xOf = (position: number): number => PAD_LEFT + position * plotWidth;
  const yOf = (value: number): number =>
    BASELINE - (Math.min(value, DISPLAY_NDVI_AXIS_MAX) / DISPLAY_NDVI_AXIS_MAX) * PLOT_HEIGHT;

  // Width comes from the count, position from the date, and nothing used to connect them:
  // at 4.48 px per day and a 11.9 px column, dates one day apart overlapped by 62%. Over
  // Russia at 55-65° N consecutive-day passes are routine.
  const gapPx = minGapFraction(model.columns.map((column) => column.position)) * plotWidth;
  const width = Math.max(3, Math.min(columnWidth(model.columns.length, plotWidth), gapPx * 0.8));
  const hitPad = Math.max(0, Math.min(3, (gapPx - width) / 2));

  const stagger =
    model.columns.length > 0 ? Math.min(12, REVEAL_TOTAL_MS / model.columns.length) : 0;
  const bracketDelay = Math.round(stagger * model.columns.length) + 40;

  if (model.columns.length === 0) {
    return (
      <div className="k-strip" ref={wrapper}>
        <div className="k-strip__empty k-note k-note--warn">{T.strip.empty}</div>
      </div>
    );
  }

  // У ГРАФИКА СВОЙ СЧЁТ, и считается он по КОЛОНКАМ С ПОСЧИТАННЫМ NDVI, а не по всем
  // съёмкам окна. `model.total` здесь и был бы вторым экземпляром того же знаменателя,
  // который печатает summary блока снимков, — то есть ровно тем спором двух счётчиков,
  // ради снятия которого подпись и написана (UX_SPEC §11.5). Числа расходятся всегда,
  // когда хоть одна съёмка не дала NDVI: у отклонённых его нет по определению.
  const measured = model.columns.filter((column) => column.ndvi !== null).length;
  const summary = [
    T.strip.graphNote(measured),
    `${T.qa.clean}: ${model.acceptedCount - model.uncertainCount}`,
    `${T.qa.uncertain}: ${model.uncertainCount}`,
    `${T.qa.rejected}: ${model.rejectedCount}`,
  ].join("; ");

  const selected = model.columns.find((column) => column.date === selectedDate) ?? null;
  const tip = hovered ? tooltipBox(xOf(hovered.position), viewWidth) : null;

  return (
    <div className={`k-strip${animate ? " k-strip--animate" : ""}`} key={revealKey} ref={wrapper}>
      <svg
        className="k-strip__svg"
        viewBox={`0 0 ${viewWidth} ${VIEW_HEIGHT}`}
        height={VIEW_HEIGHT}
        role="img"
        aria-label={`${T.strip.title}. ${summary}`}
      >
        <defs>
          {(["vegetation", "stubble", "bare", "unknown"] as const).map((tone) => (
            <pattern
              key={tone}
              id={`${idPrefix}-hatch-${tone}`}
              width="6"
              height="6"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <line
                className={`k-strip__hatch k-strip__hatch--${tone}`}
                x1="0"
                y1="0"
                x2="0"
                y2="6"
                strokeWidth="2.4"
              />
            </pattern>
          ))}
          <pattern
            id={`${idPrefix}-stale`}
            width="8"
            height="8"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(-45)"
          >
            <line className="k-strip__stalehatch" x1="0" y1="0" x2="0" y2="8" strokeWidth="1.4" />
          </pattern>
        </defs>

        {/* Bands instead of a decimal grid. Very faint fill, the word written on the band
            itself: the class is then legible without hovering and without the legend, and
            colour becomes honestly redundant because POSITION repeats it. */}
        <g className="k-strip__bands" data-testid="strip-bands" aria-hidden="true">
          {BANDS.map((band) => {
            const top = yOf(band.to);
            const bottom = yOf(band.from);
            return (
              <g key={band.key}>
                <rect
                  className={`k-strip__band k-strip__band--${band.key}`}
                  x={PAD_LEFT}
                  y={top}
                  width={Math.max(plotWidth, 0)}
                  height={Math.max(bottom - top, 0)}
                />
                <text
                  className="k-strip__bandlabel"
                  x={PAD_LEFT + 4}
                  y={(top + bottom) / 2 + 3}
                  data-band={band.key}
                >
                  {band.label}
                </text>
                {band.from > 0 && (
                  <>
                    <line
                      className="k-strip__bandedge"
                      x1={PAD_LEFT}
                      y1={bottom}
                      x2={viewWidth - PAD_RIGHT}
                      y2={bottom}
                    />
                    <text
                      className="k-strip__gridlabel"
                      x={PAD_LEFT - 6}
                      y={bottom + 3}
                      textAnchor="end"
                    >
                      {band.from.toFixed(2)}
                    </text>
                  </>
                )}
              </g>
            );
          })}

          {/* The latch hairline (final_detector min_canopy_ndvi, labelled «снова зелёное») is
              GONE from this axis. It printed a gate value together with the name of what the
              gate does — a piece of the rule, drawn beside a picture of observations. The
              class bands stay: reading a surface class off greenness is textbook, not ours. */}
        </g>

        {/* The quantity, named at the axis itself.
            PAD_LEFT is the whole label gutter — 28 px of it usable — and it does not grow
            with the console, so anything wider than the "0.25" beneath it is clipped by
            `.k-console__body`'s overflow at EVERY width. "шкала NDVI" was 32 px too wide and
            reached the reader as "ла NDVI". Four characters fit; the sentence that says what
            NDVI is lives above the chart in HTML, where text can wrap. */}
        <text className="k-strip__gridlabel" x={PAD_LEFT - 6} y={PLOT_TOP - 4} textAnchor="end">
          {T.strip.scale}
        </text>

        {/* Staleness: the right-hand edge is shaded, not silently empty. */}
        {model.stale && (
          <g className="k-strip__stale">
            <rect
              x={xOf(model.stale.fromPosition)}
              y={PLOT_TOP - 6}
              width={Math.max(viewWidth - PAD_RIGHT - xOf(model.stale.fromPosition), 0)}
              height={BASELINE - PLOT_TOP + 6}
              fill={`url(#${idPrefix}-stale)`}
            />
            <line
              className="k-strip__staleedge"
              x1={xOf(model.stale.fromPosition)}
              y1={PLOT_TOP - 6}
              x2={xOf(model.stale.fromPosition)}
              y2={BASELINE}
            />
            <text
              className="k-strip__stalelabel"
              x={viewWidth - PAD_RIGHT - 4}
              y={PLOT_TOP + 6}
              textAnchor="end"
            >
              {model.stale.label}
            </text>
          </g>
        )}

        {/* The event bracket. Its width is the uncertainty interval — that is the point. */}
        {model.event && (
          <g
            className="k-strip__bracket"
            style={animate ? { animationDelay: `${bracketDelay}ms` } : undefined}
            data-testid="strip-bracket"
            data-span-days={model.event.spanDays}
          >
            <line
              x1={xOf(model.event.fromPosition)}
              y1={BRACKET_BOTTOM}
              x2={xOf(model.event.fromPosition)}
              y2={BRACKET_TOP + 8}
            />
            <line
              x1={xOf(model.event.toPosition)}
              y1={BRACKET_BOTTOM}
              x2={xOf(model.event.toPosition)}
              y2={BRACKET_TOP + 8}
            />
            <line
              x1={xOf(model.event.fromPosition)}
              y1={BRACKET_TOP + 8}
              x2={xOf(model.event.toPosition)}
              y2={BRACKET_TOP + 8}
            />
            <text
              className="k-strip__bracketlabel"
              x={(xOf(model.event.fromPosition) + xOf(model.event.toPosition)) / 2}
              y={BRACKET_TOP + 2}
              textAnchor="middle"
            >
              {model.event.label}
            </text>
          </g>
        )}

        {/* Columns. Accepted ones stand on the value axis; rejected ones ride the rail. */}
        <g>
          {model.columns.map((column, index) => {
            const x = xOf(column.position) - width / 2;
            const height = column.accepted
              ? Math.max(3, column.level * PLOT_HEIGHT)
              : RAIL_BOTTOM - RAIL_TOP;
            const y = column.accepted ? BASELINE - height : RAIL_TOP;
            const fill =
              column.variant === "filled"
                ? undefined
                : column.variant === "hatched"
                  ? `url(#${idPrefix}-hatch-${column.tone})`
                  : "none";
            return (
              <g
                key={column.key}
                className={`k-strip__col ${toneClass(column)} k-strip__col--${column.variant}${
                  column.date === selectedDate ? " k-strip__col--selected" : ""
                }`}
                data-testid="strip-column"
                data-variant={column.variant}
                data-tone={column.tone}
                data-date={column.date ?? ""}
                data-accepted={column.accepted ? "true" : "false"}
                data-railed={column.accepted ? "false" : "true"}
                style={animate ? { animationDelay: `${Math.round(index * stagger)}ms` } : undefined}
                tabIndex={0}
                role="button"
                aria-label={column.ariaLabel}
                onMouseEnter={() => setHovered(column)}
                onMouseLeave={() => setHovered((current) => (current === column ? null : current))}
                onFocus={() => setHovered(column)}
                onBlur={() => setHovered((current) => (current === column ? null : current))}
                onClick={() => column.date && onPickDate?.(column.date)}
                // An SVG <g> has no native activation, so Enter/Space do not synthesise a
                // click: without this the column looked focusable and did nothing.
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  if (column.date) onPickDate?.(column.date);
                }}
              >
                <title>{column.ariaLabel}</title>
                <rect
                  className="k-strip__bar"
                  x={x}
                  y={y}
                  width={width}
                  height={height}
                  fill={fill}
                />
                {/* Hit area widened only into the empty half-gap: at 4 px between dates a
                    generous pad stole the neighbour's hover. */}
                <rect
                  className="k-strip__hit"
                  x={x - hitPad}
                  y={PLOT_TOP - 8}
                  width={width + 2 * hitPad}
                  height={RAIL_BOTTOM - PLOT_TOP + 11}
                />
              </g>
            );
          })}
        </g>

        {/* The selected date, marked on the strip itself — the readout below says what it is. */}
        {selected && (
          <line
            className="k-strip__selected"
            data-testid="strip-selected"
            x1={xOf(selected.position)}
            y1={PLOT_TOP - 10}
            x2={xOf(selected.position)}
            y2={RAIL_BOTTOM}
          />
        )}

        {/* Baseline: the heavy rule of a topographic sheet. */}
        <line
          className="k-strip__axis"
          x1={PAD_LEFT - 8}
          y1={BASELINE}
          x2={viewWidth - PAD_RIGHT}
          y2={BASELINE}
        />

        {/* The rail used to be named here, and never actually was: "отклонена" is 49 px of
            mono in a 28 px gutter, so it reached the reader as "клонена" — the same overflow
            that ate the axis title, found while fixing it. The legend directly below names it
            with a swatch drawn low and dashed, i.e. a picture of the rail, and every column
            carries the word in its <title> and aria-label. A clipped label names nothing. */}

        {/* Счётчика недатированных съёмок на графике БОЛЬШЕ НЕТ (2026-09-05, закон
            зачистки): «съёмка без даты» — отчёт о нашем конвейере, не о поле. Учёт таких
            съёмок остаётся журналу яруса 3 — `RejectedLog` читает `model.undated`. */}

        {model.ticks.map((tick) => (
          <g key={`${tick.position}-${tick.label}`}>
            <line
              className="k-strip__tick"
              x1={xOf(tick.position)}
              y1={RAIL_BOTTOM}
              x2={xOf(tick.position)}
              y2={TICK_BOTTOM}
            />
            <text
              className="k-strip__ticklabel"
              x={xOf(tick.position)}
              y={TICK_LABEL_Y}
              textAnchor="middle"
            >
              {tick.label}
            </text>
          </g>
        ))}

        <g>
          <line
            className="k-strip__today"
            x1={viewWidth - PAD_RIGHT}
            y1={PLOT_TOP - 10}
            x2={viewWidth - PAD_RIGHT}
            y2={TICK_BOTTOM}
          />
          <text
            className="k-strip__ticklabel"
            x={viewWidth - PAD_RIGHT}
            y={TICK_LABEL_Y}
            textAnchor="end"
          >
            {T.strip.today}
          </text>
        </g>
      </svg>

      {hovered && tip && (
        <div
          className="k-strip__tip"
          role="tooltip"
          data-testid="strip-tooltip"
          style={{
            left: `${tip.left}px`,
            minWidth: `${tip.minWidth}px`,
            maxWidth: `${tip.maxWidth}px`,
            top: `${TIP_TOP}px`,
          }}
        >
          <div className="k-strip__tipdate k-mono">{formatDate(hovered.date)}</div>
          <div className="k-strip__tipstate">{stateWords(hovered)}</div>
          {dev && (
            <dl className="k-readout k-strip__tipnumbers">
              <dt>{T.strip.ndvi}</dt>
              <dd>{num(hovered.ndvi, 3)}</dd>
              <dt>{T.strip.ndmi}</dt>
              <dd>{num(hovered.ndmi, 3)}</dd>
              <dt>{T.strip.bsi}</dt>
              <dd>{num(hovered.bsi, 3)}</dd>
              <dt>{T.strip.valid}</dt>
              <dd>{percent(hovered.validFraction)}</dd>
              <dt>{T.strip.bad}</dt>
              <dd>{percent(hovered.badFraction)}</dd>
              <dt>{T.strip.snow}</dt>
              <dd>{percent(hovered.snowFraction)}</dd>
            </dl>
          )}
        </div>
      )}

      {/* Zero height when nothing is selected: the fold budget above the verdict is spent. */}
      {selected && (
        <p className="k-strip__readout k-mono" data-testid="strip-readout">
          {T.strip.selected(formatDayMonthShort(selected.date), stateWords(selected))}
        </p>
      )}

      <div className="k-strip__legend">
        <span className="k-strip__legenditem">
          <svg className="k-strip__swatch" viewBox="0 0 12 12" aria-hidden="true">
            <rect className="k-strip__swatchfill" x="1" y="1" width="10" height="10" />
          </svg>
          {T.qa.clean}
        </span>
        <span className="k-strip__legenditem">
          <svg className="k-strip__swatch" viewBox="0 0 12 12" aria-hidden="true">
            <defs>
              <pattern
                id={`${idPrefix}-legendhatch`}
                width="4"
                height="4"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                <line
                  className="k-strip__hatch k-strip__hatch--legend"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="4"
                  strokeWidth="2"
                />
              </pattern>
            </defs>
            <rect
              className="k-strip__swatchoutline"
              x="1"
              y="1"
              width="10"
              height="10"
              fill={`url(#${idPrefix}-legendhatch)`}
            />
          </svg>
          {T.qa.uncertain}
        </span>
        <span className="k-strip__legenditem">
          <svg className="k-strip__swatch" viewBox="0 0 12 12" aria-hidden="true">
            <rect className="k-strip__swatchreject" x="1" y="4" width="10" height="5" fill="none" />
          </svg>
          {T.qa.rejected}
        </span>
        <span className="k-strip__legenditem">
          <svg className="k-strip__swatch" viewBox="0 0 12 12" aria-hidden="true">
            <g className="k-strip__swatchbracket">
              <line x1="2" y1="9" x2="2" y2="3" />
              <line x1="10" y1="9" x2="10" y2="3" />
              <line x1="2" y1="3" x2="10" y2="3" />
            </g>
          </svg>
          {T.strip.legendBracket}
        </span>
      </div>

      {/* Подпись под графиком, а не над блоком: у графика свой счёт — даты, на которых NDVI
          вообще посчитан, — и он МЕНЬШЕ числа съёмок окна. Без подписи два числа в одном
          блоке читались как спор (UX_SPEC §11.5). */}
      <p className="k-hint k-strip__graphnote" data-testid="strip-graphnote">
        {T.strip.graphNote(measured)}
      </p>
    </div>
  );
}
